import {
  personBelowActiveSentence,
  personCapacitySentence,
  personCarriedSentence,
  personKindOfUnit,
  type PersonKind,
} from "./person-quantity";

/**
 * The Staff add-on is sold per employee and is a prepaid quantity, like the terminals (ADR-0015 addendum
 * of 2026-10-01). Three of its rules bind it to the rest of the subscription, and each has a sentence a
 * customer can act on. They are written ONCE because the same fact is read in three places — ahead of the
 * call (the configurator's preview), after it (the api's refusal) and in a toast — and a sentence written
 * three times is a sentence that says three things.
 *
 * M20 (Z-30): Student Attendance is sold per student on the same rules, so the sentences now live in
 * `person-quantity.ts` over every per-person kind; these are Staff's names for them, kept for M19's callers.
 */

const STAFF: PersonKind = personKindOfUnit("employee")!;

/**
 * Rule 3 — Staff may never fall below the employees who are active (`409 STAFF_QUANTITY_BELOW_ACTIVE`):
 * "80 employees are active; exit employees before reducing Staff to 50." Asking for none at all is
 * cancelling, and the sentence says so rather than "reducing Staff to 0".
 */
export function staffBelowActiveSentence(active: number, requested: number): string {
  return personBelowActiveSentence(STAFF, active, requested);
}

/**
 * Rule 2 — Staff may not exceed the organisation's people capacity (`422 QUANTITY_OUT_OF_RANGE` with
 * `peopleCapacity` in its details). Both numbers, and the two ways out.
 */
export function staffAbovePeopleSentence(staff: number, peopleCapacity: number): string {
  return personCapacitySentence(peopleCapacity, { staff });
}

/**
 * Rule 2, the other half — a people reduction that leaves Staff above the new capacity carries Staff down
 * with it, in the same change. This is what the configurator says BEFORE the owner confirms one.
 */
export function staffCarriedSentence(to: number): string {
  return personCarriedSentence([{ kind: STAFF, to }]);
}
